import { FileTypeEnum } from '$/utils/upload';
import { create } from 'zustand';

/** 图片变高清类型：对应 magicsr 接口 hd_type */
export enum ImageHdType {
  /** 高清 */
  HD = 'hd',
  /** 超清 */
  Super = 'super',
  /** AI 超清 */
  AISuper = 'ai_super',
  /** 人像 */
  Portrait = 'portrait',
  /** 人像 PRO */
  PortraitPro = 'portrait_pro',
  /** 文本 */
  Text = 'text',
  /** 商品 */
  Goods = 'goods',
  /** 老照片 */
  OldPicture = 'old_photo',
}
/** 视频变高清模式：值对应 video_hd 接口 hd_type */
export enum VideoSRMode {
  /** 2K */
  _2K = '2k',
  /** 4K */
  _4K = '4k',
  /** AI 4K+ */
  AI4KPlus = 'ai_4k',
  /** 人像 */
  Portrait = 'portrait',
  /** 商品 */
  Goods = 'goods',
  /** 夜景 */
  LowLight = 'night',
  /** 去 AI */
  DeAI = 'de_ai',
  /** 游戏 */
  Game = 'game',
  /** 动漫 */
  Anime = 'anime',
}

export type BatchImageStatus = 'normal' | 'processing' | 'success' | 'failed' | 'unrecognized' | 'downloadable';

export interface BatchImageItem {
  id: string;
  status: BatchImageStatus;
  url: string;
  type?: FileTypeEnum;
  fileName?: string;
  /** 视频封面图 URL（视频项可选） */
  coverUrl?: string;
  /** 视频素材展示时长（秒），保留原有展示口径 */
  duration?: number;
  /** 视频素材接口参数时长（秒），用于 cost/submit params.video_duration */
  requestDuration?: number;
  width?: number;
  height?: number;
  resultUrl?: string;
  /** 已拿到 resultUrl 但结果图仍在加载中，用于保持 loading 遮罩直到图片 onLoad */
  resultImageLoading?: boolean;
  progress?: number;
  errorText?: string;
  selected?: boolean;
  selectable?: boolean;
  /** 服务端任务 id（msgid），创建任务后由 ids 映射绑定 */
  taskId?: string;
}

interface AIEnhanceStore {
  singleOriginalImage: string;
  singleResultImage: string;
  /** 单张模式最近一次生成结果对应的服务端任务 id。 */
  singleResultTaskId: string;
  singleCompareIng: boolean;
  /** 单张上传的媒体类型：image 或 video */
  singleMediaType: 'image' | 'video';
  /** 单张视频素材展示时长（秒），保留原有展示口径 */
  singleVideoDuration?: number;
  /** 单张视频素材接口参数时长（秒），用于 cost/submit params.video_duration */
  singleVideoRequestDuration?: number;
  mode: 'single' | 'batch';
  singleEraserLoading: boolean;
  batchImages: BatchImageItem[];
  batchMaxCount: number;
  batchProcessingMode: boolean;
  previewImageIndex: number | null;
  /** 批量处理进行中 */
  batchEraserLoading: boolean;
  /** 单张模式「重新上传」触发打开文件选择 */
  singleReuploadSignal: number;
  /** 批量模式「重新上传」触发打开文件选择 */
  batchReuploadSignal: number;
  /** 批量模式：点击重新上传后为 true，下次选择图片确认上传后替换列表并置回 false */
  batchReuploadPending: boolean;
  /** 图片变高清类型（高清/超清/AI超清 与 智能识别场景互斥） */
  imageHdType: ImageHdType;
  /** 视频变高清模式 */
  videoSRMode: VideoSRMode | undefined;
}

const useAIEnhanceStore = create<AIEnhanceStore>(() => ({
  singleOriginalImage: '',
  singleResultImage: '',
  singleResultTaskId: '',
  singleCompareIng: false,
  singleMediaType: 'image',
  singleVideoDuration: undefined,
  singleVideoRequestDuration: undefined,
  mode: 'single',
  singleEraserLoading: false,
  batchImages: [],
  batchMaxCount: 30,
  batchProcessingMode: false,
  previewImageIndex: null,
  batchEraserLoading: false,
  singleReuploadSignal: 0,
  batchReuploadSignal: 0,
  batchReuploadPending: false,
  imageHdType: ImageHdType.HD,
  videoSRMode: VideoSRMode._2K,
}));

export default useAIEnhanceStore;

export function setSingleOriginalImage(singleOriginalImage: string) {
  useAIEnhanceStore.setState(() => ({ singleOriginalImage }));
}

/**
 * 设置单张模式结果图，清空结果时同步清空任务 id。
 */
export function setSingleResultImage(singleResultImage: string) {
  useAIEnhanceStore.setState(() => ({
    singleResultImage,
    ...(!singleResultImage ? { singleResultTaskId: '' } : {}),
  }));
}

/**
 * 设置单张模式结果对应的服务端任务 id。
 */
export function setSingleResultTaskId(singleResultTaskId: string) {
  useAIEnhanceStore.setState(() => ({ singleResultTaskId }));
}

export function setSingleMediaType(singleMediaType: 'image' | 'video') {
  useAIEnhanceStore.setState(() => ({ singleMediaType }));
}

/**
 * 记录单张视频素材展示时长，保留原有展示口径。
 */
export function setSingleVideoDuration(singleVideoDuration?: number) {
  useAIEnhanceStore.setState(() => ({ singleVideoDuration }));
}

/**
 * 记录单张视频素材接口参数时长，供 cost/submit 写入 params.video_duration。
 */
export function setSingleVideoRequestDuration(singleVideoRequestDuration?: number) {
  useAIEnhanceStore.setState(() => ({ singleVideoRequestDuration }));
}

/**
 * 设置当前模式
 * 切到单张时退出批量处理
 */
export function setMode(mode: 'single' | 'batch') {
  if (mode === 'single') {
    useAIEnhanceStore.setState((state) => ({
      mode: 'single',
      batchProcessingMode: false,
      previewImageIndex: null,
      batchImages: state.batchImages.map((item) => ({ ...item, selected: false })),
    }));
  } else {
    useAIEnhanceStore.setState(() => ({ mode: 'batch' }));
  }
}

export function setSingleEraserLoading(singleEraserLoading: boolean) {
  useAIEnhanceStore.setState(() => ({ singleEraserLoading }));
}

export function setSingleCompareIng(singleCompareIng: boolean) {
  useAIEnhanceStore.setState(() => ({ singleCompareIng }));
}

/**
 * 设置图片变高清类型。
 * 高清/超清/AI超清 与智能识别场景共用同一字段，互斥切换。
 */
export function setImageHdType(imageHdType: ImageHdType) {
  useAIEnhanceStore.setState(() => ({ imageHdType }));
}

export function setVideoSRMode(videoSRMode: VideoSRMode | undefined) {
  useAIEnhanceStore.setState(() => ({ videoSRMode }));
}

export function updateBatchImage(
  id: string,
  patch: Partial<Pick<BatchImageItem, 'status' | 'progress' | 'resultUrl' | 'resultImageLoading' | 'errorText' | 'taskId' | 'width' | 'height'>>,
) {
  useAIEnhanceStore.setState((state) => ({
    batchImages: state.batchImages.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  }));
}

export function setBatchEraserLoading(loading: boolean) {
  useAIEnhanceStore.setState(() => ({ batchEraserLoading: loading }));
}

export function setBatchImages(batchImages: BatchImageItem[]) {
  useAIEnhanceStore.setState(() => ({ batchImages }));
}

export function addBatchImages(newItems: BatchImageItem[]) {
  useAIEnhanceStore.setState((state) => ({
    batchImages: [...newItems, ...state.batchImages],
  }));
}

export function setBatchProcessingMode(active: boolean) {
  useAIEnhanceStore.setState((state) => {
    const batchImages = active
      ? state.batchImages
      : state.batchImages.map((item) => ({ ...item, selected: false }));
    return { batchProcessingMode: active, batchImages };
  });
}

export function toggleBatchImageSelected(id: string) {
  useAIEnhanceStore.setState((state) => {
    const batchImages = state.batchImages.map((item) => {
      if (item.id === id) return { ...item, selected: !item.selected };
      return item;
    });
    return { batchImages };
  });
}

export function removeBatchImage(id: string) {
  useAIEnhanceStore.setState((state) => {
    const { batchImages: stateBatchImages, previewImageIndex } = state;
    const removedIndex = stateBatchImages.findIndex((img) => img.id === id);
    const batchImages = stateBatchImages.filter((img) => img.id !== id);

    let nextPreviewIndex = previewImageIndex;
    if (previewImageIndex !== null && removedIndex >= 0) {
      if (removedIndex === previewImageIndex) {
        nextPreviewIndex = null;
      } else if (removedIndex < previewImageIndex) {
        nextPreviewIndex = Math.max(0, previewImageIndex - 1);
      }
    }

    return { batchImages, previewImageIndex: nextPreviewIndex };
  });
}

export function setPreviewImageIndex(index: number | null) {
  useAIEnhanceStore.setState(() => ({ previewImageIndex: index }));
}

export function cancelBatchSelection() {
  useAIEnhanceStore.setState((state) => ({
    batchProcessingMode: false,
    batchImages: state.batchImages.map((item) => ({ ...item, selected: false })),
  }));
}

export function triggerBatchReupload() {
  useAIEnhanceStore.setState((state) => ({
    batchReuploadSignal: state.batchReuploadSignal + 1,
  }));
}

export function triggerSingleReupload() {
  useAIEnhanceStore.setState((state) => ({
    singleReuploadSignal: state.singleReuploadSignal + 1,
  }));
}

export function setBatchReuploadPending(pending: boolean) {
  useAIEnhanceStore.setState(() => ({ batchReuploadPending: pending }));
}

/**
 * 重置 AI Enhance 页面 store，避免再次进入时沿用旧数据
 */
export function resetAIEnhanceStore() {
  useAIEnhanceStore.setState(() => ({
    singleOriginalImage: '',
    singleResultImage: '',
    singleResultTaskId: '',
    singleCompareIng: false,
    singleMediaType: 'image',
    singleVideoDuration: undefined,
    singleVideoRequestDuration: undefined,
    mode: 'single',
    singleEraserLoading: false,
    batchImages: [],
    batchProcessingMode: false,
    previewImageIndex: null,
    batchEraserLoading: false,
    singleReuploadSignal: 0,
    batchReuploadSignal: 0,
    batchReuploadPending: false,
    imageHdType: ImageHdType.HD,
    videoSRMode: VideoSRMode._2K,
  }));
}
